import { useCallback, useEffect, useRef } from 'react'

type OverflowState = 'none' | 'top' | 'bottom' | 'both'

/** Tolerance in pixels for sub-pixel scroll positions at the extremes. */
const TOLERANCE = 3

function computeState(el: HTMLElement): OverflowState {
  const { scrollTop, scrollHeight, clientHeight } = el
  const canScroll = scrollHeight > clientHeight + TOLERANCE
  if (!canScroll) return 'none'
  const atTop = scrollTop <= TOLERANCE
  const atBottom = scrollTop + clientHeight >= scrollHeight - TOLERANCE
  if (atTop && atBottom) return 'none'
  if (atTop) return 'bottom'
  if (atBottom) return 'top'
  return 'both'
}

/**
 * Keeps `data-overflow` on a scrolling container in sync with its scroll
 * position, using the exact value vocabulary the `.aiScrollFade[data-overflow]`
 * CSS rules expect:
 *
 * - `"none"` — content fits or container is not scrollable (attribute absent/none)
 * - `"top"`  — scrolled away from top edge, at the bottom (shadow at top)
 * - `"bottom"` — at the top, more content below (shadow at bottom)
 * - `"both"` — scrolled, content in both directions (shadows on both edges)
 *
 * Updates on `scroll`, `resize` (ResizeObserver on container + children),
 * and on structural DOM changes (MutationObserver for childList). Every
 * listener is cleaned up on unmount.
 *
 * Usage:
 * ```tsx
 * const scrollRef = useRef<HTMLDivElement>(null)
 * useOverflowShadow(scrollRef)
 * // then: <div ref={scrollRef} className="aiScrollFade …">
 * ```
 */
export function useOverflowShadow<T extends HTMLElement = HTMLDivElement>(
  ref: React.RefObject<T | null>
): void {
  // Keep a stable ref to the current measure callback so listeners don't stale-close
  const measureRef = useRef<() => void>(() => undefined)

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const next = computeState(el)
    el.dataset.overflow = next === 'none' ? '' : next
  }, [ref])

  // Always keep measureRef current
  useEffect(() => {
    measureRef.current = measure
  })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // Initial measurement
    measure()

    const handleScroll = () => measureRef.current()

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => measureRef.current()) : null
    if (ro) {
      ro.observe(el)
      for (const child of Array.from(el.children)) {
        ro.observe(child as Element)
      }
    }

    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(() => {
      // Re-register children after structural changes
      if (ro) {
        ro.disconnect()
        ro.observe(el)
        for (const child of Array.from(el.children)) {
          ro.observe(child as Element)
        }
      }
      measureRef.current()
    }) : null
    mo?.observe(el, { childList: true })

    el.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      el.removeEventListener('scroll', handleScroll)
      ro?.disconnect()
      mo?.disconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref])
}
